/* ==========================================================================
   procedural-sea-rocks.js
   Dark, jagged, faceted sea rock — the kind coral colonises.

   No dependencies. Generation is pure math, so it runs in Node as happily as
   in a browser. THREE is only touched by the optional toBufferGeometry()
   helper, which you pass in yourself.

     const r  = SeaRocks.build({ kind:'outcrop', seed:7, size:2.4 });
     const g  = SeaRocks.toBufferGeometry(THREE, r);
     const m  = new THREE.Mesh(g, new THREE.MeshStandardMaterial({
                  vertexColors:true, roughness:0.95, metalness:0.0,
                  flatShading:false }));   // normals are already per-face

     r.attach   // [{p:[x,y,z], n:[x,y,z], area}]  where coral can sit

   WHY IT LOOKS SHARP

   Rock is not a displaced sphere. Noise on a sphere gives lumps, and lumps
   read as potatoes no matter how dark you make them. These are built by
   CUTTING: start from a cube and slice it with a set of planes, which yields
   genuinely flat faces meeting at genuinely hard edges.

   The plane normals are drawn from three cleavage axes rather than uniformly
   over the sphere, so facets repeat and align the way fractured rock does
   instead of carving down to a random pebble. Several convex chunks are then
   stacked into one rock, which is what supplies the concavity, overhangs and
   spires a single convex solid can never have.

   Normals are emitted per face, never averaged. Averaging is what would
   round the creases straight back off.

   KINDS
     boulder   one or two blocks, roughly equant
     outcrop   a low jagged ridge — the workhorse for a reef floor
     spire     tall, tilted, tapering
     stack     slabby blocks piled with ledges
     rubble    scattered angular fragments
   ========================================================================== */
'use strict';

/* ---------- scalar + vector ---------- */
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;
function sstep(e0, e1, x) { const t = clamp((x - e0) / (e1 - e0), 0, 1); return t * t * (3 - 2 * t); }
const add = (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const mul = (a, s) => [a[0] * s, a[1] * s, a[2] * s];
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const len = a => Math.hypot(a[0], a[1], a[2]);
function norm(a) { const l = len(a); return l > 1e-12 ? [a[0] / l, a[1] / l, a[2] / l] : [0, 1, 0]; }
function perp(t) { const a = Math.abs(t[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0]; return norm(cross(a, t)); }

function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function makeNoise3(seed) {
  const rnd = mulberry32(seed >>> 0), P = new Float32Array(1024);
  for (let i = 0; i < 1024; i++) P[i] = rnd();
  function h3(x, y, z) {
    let n = (x * 374761393 + y * 668265263 + z * 1442695041) | 0;
    n = ((n ^ (n >> 13)) * 1274126177) | 0;
    return P[(n >>> 0) & 1023];
  }
  return function (x, y, z) {
    const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
    const xf = x - xi, yf = y - yi, zf = z - zi;
    const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf), w = zf * zf * (3 - 2 * zf);
    const a = lerp(h3(xi, yi, zi), h3(xi + 1, yi, zi), u), b = lerp(h3(xi, yi + 1, zi), h3(xi + 1, yi + 1, zi), u);
    const c = lerp(h3(xi, yi, zi + 1), h3(xi + 1, yi, zi + 1), u), d = lerp(h3(xi, yi + 1, zi + 1), h3(xi + 1, yi + 1, zi + 1), u);
    return lerp(lerp(a, b, v), lerp(c, d, v), w);
  };
}
function fbm(nz, p, f, oct, gain) {
  let a = 1, s = 0, n = 0; oct = oct || 3; gain = gain === undefined ? 0.5 : gain;
  for (let i = 0; i < oct; i++) { s += a * (nz(p[0] * f, p[1] * f, p[2] * f) - 0.5) * 2; n += a; a *= gain; f *= 2.03; }
  return s / n;
}
const mixc = (a, b, t) => [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)];
const mulc = (c, k) => [c[0] * k, c[1] * k, c[2] * k];

/* ==========================================================================
   CONVEX SOLID BY PLANE CUTTING

   A polyhedron is a list of faces, each an ordered ring of points with an
   outward-facing winding. Cutting keeps the half-space dot(x,n) <= d: every
   face is clipped, and the new cross-section is capped with a fresh face.
   Because the solid stays convex, that cross-section is convex too, so the
   cap can simply be sorted by angle about its own centroid.
   ========================================================================== */
function faceNormal(poly) {                       /* Newell — robust for n-gons */
  let n = [0, 0, 0];
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    n[0] += (a[1] - b[1]) * (a[2] + b[2]);
    n[1] += (a[2] - b[2]) * (a[0] + b[0]);
    n[2] += (a[0] - b[0]) * (a[1] + b[1]);
  }
  return norm(n);
}
function centroid(pts) {
  const c = [0, 0, 0];
  for (const p of pts) { c[0] += p[0]; c[1] += p[1]; c[2] += p[2]; }
  return mul(c, 1 / pts.length);
}
function startCube(r) {
  const v = [[-r,-r,-r],[r,-r,-r],[r,r,-r],[-r,r,-r],[-r,-r,r],[r,-r,r],[r,r,r],[-r,r,r]];
  const f = [[0,3,2,1],[4,5,6,7],[0,1,5,4],[1,2,6,5],[2,3,7,6],[3,0,4,7]];
  return f.map(q => q.map(i => v[i].slice()));
}
function cut(faces, pn, pd) {
  const EPS = 1e-9, out = [], rim = [];
  for (const f of faces) {
    const res = [], n = f.length;
    for (let i = 0; i < n; i++) {
      const a = f[i], b = f[(i + 1) % n];
      const da = dot(a, pn) - pd, db = dot(b, pn) - pd;
      if (da <= EPS) res.push(a);
      if ((da > EPS && db < -EPS) || (da < -EPS && db > EPS)) {
        const t = da / (da - db);
        const p = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
        res.push(p); rim.push(p);
      }
    }
    if (res.length >= 3) out.push(res);
  }
  if (rim.length >= 3) {
    const c = centroid(rim), u = perp(pn), v = cross(pn, u);
    rim.sort((p, q) =>
      Math.atan2(dot(sub(p, c), v), dot(sub(p, c), u)) -
      Math.atan2(dot(sub(q, c), v), dot(sub(q, c), u)));
    const cap = [];
    for (const p of rim) {
      const last = cap[cap.length - 1];
      if (!last || len(sub(p, last)) > 1e-7) cap.push(p);
    }
    if (cap.length >= 3 && len(sub(cap[0], cap[cap.length - 1])) < 1e-7) cap.pop();
    if (cap.length >= 3) {
      if (dot(faceNormal(cap), pn) < 0) cap.reverse();
      out.push(cap);
    }
  }
  return out;
}

/* A chunk's plane normals come from three cleavage axes rather than a uniform
   sphere. Uniform directions carve toward a rounded pebble; clustering them
   makes facets repeat and align, which is what fractured rock does. */
function chunk(rnd, jag, squash) {
  const ax = [];
  const a0 = norm([rnd() - 0.5, rnd() - 0.5, rnd() - 0.5]);
  ax.push(a0);
  ax.push(norm(cross(a0, [rnd() - 0.5, rnd() - 0.5, rnd() - 0.5])));
  ax.push(norm(cross(ax[0], ax[1])));

  let faces = startCube(2.2);

  /* Bound the solid FIRST with directions spread over the whole sphere.
     Cleavage cuts alone never reach the starting cube's corners — those sit
     54.7 degrees off every axis, far outside the jitter — so the corners
     survive as long spikes and the rock comes out as broken glass. A
     Fibonacci sphere of cuts turns it into a rough equant polyhedron; the
     cleavage cuts then flatten that into a block. */
  const NB = 18;
  for (let i = 0; i < NB; i++) {
    const y = 1 - (i + 0.5) / NB * 2;
    const rr = Math.sqrt(Math.max(0, 1 - y * y)), th = i * 2.39996;
    const bn = [Math.cos(th) * rr, y, Math.sin(th) * rr];
    faces = cut(faces, bn, 0.90 + rnd() * 0.26);
  }

  const N = 11 + (rnd() * 12 | 0);
  for (let i = 0; i < N; i++) {
    const a = ax[(rnd() * 3) | 0], s = rnd() < 0.5 ? -1 : 1;
    /* jitter around the cleavage axis: 0 would give a perfect box, large
       values scatter back toward a pebble */
    const j = 0.16 + jag * 0.42;
    let n = norm([a[0] * s + (rnd() - 0.5) * j * 2,
                  a[1] * s + (rnd() - 0.5) * j * 2,
                  a[2] * s + (rnd() - 0.5) * j * 2]);
    n = norm([n[0], n[1] / Math.max(squash, 0.05), n[2]]);
    const d = 0.98 - jag * 0.30 * Math.pow(rnd(), 1.4);
    faces = cut(faces, n, d);
    if (!faces.length) break;
  }
  /* Normalise to half-extent 0.5. Without this a chunk lands somewhere
     between 0.6 and 1.0 depending on how the cuts fell, and the layout
     offsets below — which are in chunk widths — stop meaning anything, so
     blocks drift apart instead of fusing. */
  let m = 1e-6;
  for (const f of faces) for (const v of f) m = Math.max(m, Math.abs(v[0]), Math.abs(v[1]), Math.abs(v[2]));
  const k = 0.5 / m;
  /* cut() pushes vertex REFERENCES into every face that shares them, so a
     naive loop scales a shared vertex once per face it appears in — k^3 for
     a corner instead of k. That does not shrink the rock, it shatters it. */
  const done = new Set();
  for (const f of faces) for (const v of f) {
    if (done.has(v)) continue;
    done.add(v);
    v[0] *= k; v[1] *= k; v[2] *= k;
  }
  return faces;
}

/* ---------- 3x3 basis: yaw/pitch/roll plus non-uniform scale ---------- */
function basis(rnd, sx, sy, sz, tilt) {
  const ya = rnd() * Math.PI * 2, pa = (rnd() - 0.5) * tilt, ra = (rnd() - 0.5) * tilt;
  const cy = Math.cos(ya), sy2 = Math.sin(ya), cp = Math.cos(pa), sp = Math.sin(pa);
  const cr = Math.cos(ra), sr = Math.sin(ra);
  const m = [
    [cy * cp, cy * sp * sr - sy2 * cr, cy * sp * cr + sy2 * sr],
    [sy2 * cp, sy2 * sp * sr + cy * cr, sy2 * sp * cr - cy * sr],
    [-sp, cp * sr, cp * cr]
  ];
  return { m, s: [sx, sy, sz] };
}
function xform(B, p, o) {
  const q = [p[0] * B.s[0], p[1] * B.s[1], p[2] * B.s[2]];
  return [B.m[0][0] * q[0] + B.m[0][1] * q[1] + B.m[0][2] * q[2] + o[0],
          B.m[1][0] * q[0] + B.m[1][1] * q[1] + B.m[1][2] * q[2] + o[1],
          B.m[2][0] * q[0] + B.m[2][1] * q[1] + B.m[2][2] * q[2] + o[2]];
}

/* ==========================================================================
   LAYOUTS
   Several convex chunks per rock. A single convex solid can only ever be a
   crystal; the concavity, overhangs and notches come from how the blocks pile.
   ========================================================================== */
function layout(kind, rnd) {
  const out = [];
  const push = (o, sx, sy, sz, tilt, squash) =>
    out.push({ o, B: basis(rnd, sx, sy, sz, tilt), squash });

  /* Offsets are deliberately well under one chunk width. Blocks have to
     interpenetrate to read as one fractured mass; spaced apart they look
     like separate props sitting near each other. */
  if (kind === 'boulder') {
    push([0, 0.34, 0], 1.35, 1.00, 1.25, 0.6, 1.0);
    push([(rnd()-.5)*.45, 0.52+rnd()*.22, (rnd()-.5)*.45], 0.95, 0.72, 0.90, 1.0, 1.0);
    if (rnd() < 0.8) push([(rnd()-.5)*.6, 0.24+rnd()*.2, (rnd()-.5)*.6], 0.70, 0.55, 0.80, 1.2, 1.0);
  } else if (kind === 'spire') {
    const n = 4 + (rnd()*3|0);
    let y = 0.18, s = 1.25;
    const lean = (rnd()-0.5)*0.42, la = rnd()*Math.PI*2;
    for (let i = 0; i < n; i++) {
      push([Math.cos(la)*lean*y, y, Math.sin(la)*lean*y],
           s*0.78, s*0.95, s*0.72, 0.50, 1.0);
      y += s*0.42; s *= 0.80 + rnd()*0.10;        /* 0.42 step on 0.95 height = overlap */
    }
  } else if (kind === 'stack') {
    const n = 4 + (rnd()*3|0);
    let y = 0.14;
    for (let i = 0; i < n; i++) {
      const h = 0.42 + rnd()*0.30, w = 1.45 - i*0.14 + (rnd()-.5)*0.25;
      push([(rnd()-.5)*0.30, y, (rnd()-.5)*0.30], w, h, w*(0.82+rnd()*0.30), 0.34, 0.45);
      y += h*0.34;
    }
  } else if (kind === 'rubble') {
    const n = 6 + (rnd()*7|0);
    for (let i = 0; i < n; i++) {
      const s = 0.40 + rnd()*0.55;
      const a = rnd()*Math.PI*2, r = rnd()*0.62;
      push([Math.cos(a)*r, s*0.26, Math.sin(a)*r], s, s*(0.55+rnd()*0.5), s*(0.75+rnd()*0.5), 1.5, 1.0);
    }
  } else {                                        /* outcrop */
    const n = 5 + (rnd()*4|0);
    const ridge = rnd()*Math.PI*2, cr = Math.cos(ridge), sr = Math.sin(ridge);
    for (let i = 0; i < n; i++) {
      const t = (i/Math.max(1,n-1))*2 - 1;
      /* tallest in the middle, shouldering down at both ends */
      const h = (0.55 + 0.95*(1 - t*t)) * (0.75 + rnd()*0.5);
      const along = t*0.95 + (rnd()-.5)*0.18;     /* 0.95 span, ~1.0 wide chunks */
      const off = (rnd()-.5)*0.34;
      push([cr*along - sr*off, h*0.30, sr*along + cr*off],
           0.85 + rnd()*0.45, h, 0.80 + rnd()*0.40, 0.60, 0.85);
    }
  }
  return out;
}

/* ==========================================================================
   BUILD
   ========================================================================== */
const PALETTES = {
  /* Dark, but not silhouettes. Below about 0.10 the facets stop resolving
     and the rock reads as a hole in the frame rather than as stone. */
  basalt:    { dark: [0.105, 0.112, 0.128], mid: [0.215, 0.224, 0.242], lit: [0.395, 0.405, 0.418] },
  slate:     { dark: [0.098, 0.114, 0.134], mid: [0.196, 0.220, 0.252], lit: [0.352, 0.386, 0.424] },
  limestone: { dark: [0.140, 0.136, 0.124], mid: [0.278, 0.270, 0.246], lit: [0.470, 0.458, 0.424] },
  ironstone: { dark: [0.125, 0.098, 0.080], mid: [0.252, 0.196, 0.152], lit: [0.430, 0.344, 0.258] }
};
const ALGAE = [0.150, 0.192, 0.132];              /* turf film on upward faces */
const CORALLINE = [0.310, 0.192, 0.212];          /* pink crust, sparse        */

const DEFAULTS = {
  boulder: { jag: 0.62, rough: 0.055 },
  outcrop: { jag: 0.78, rough: 0.065 },
  spire:   { jag: 0.85, rough: 0.060 },
  stack:   { jag: 0.55, rough: 0.045 },
  rubble:  { jag: 0.90, rough: 0.075 }
};
const KINDS = Object.keys(DEFAULTS);

function build(opts) {
  const o = opts || {};
  const kind = DEFAULTS[o.kind] ? o.kind : 'outcrop';
  const seed = o.seed !== undefined ? o.seed : (Math.random() * 1e6) | 0;
  const size = o.size !== undefined ? o.size : 1.0;
  const jag = clamp(o.jag !== undefined ? o.jag : DEFAULTS[kind].jag, 0, 1);
  const rough = o.rough !== undefined ? o.rough : DEFAULTS[kind].rough;
  const algae = o.algae !== undefined ? o.algae : 0.55;
  const pal = PALETTES[o.palette] || PALETTES.basalt;

  const t0 = (typeof performance !== 'undefined' ? performance : Date).now();
  const rnd = mulberry32(seed >>> 0);
  const nz = makeNoise3(seed * 2654435761 + 17);

  const blocks = layout(kind, rnd).map(L => {
    const faces = chunk(rnd, jag, L.squash);
    return { faces, B: L.B, o: L.o, planes: null };
  });

  /* Each block's own half-spaces, kept so a face centroid can be tested for
     being buried inside a neighbour — that is how attachment points on hidden
     surfaces get rejected. */
  for (const b of blocks) {
    const pl = [];
    for (const f of b.faces) {
      const wf = f.map(p => xform(b.B, p, b.o));
      const n = faceNormal(wf);
      pl.push({ n, d: dot(centroid(wf), n) });
    }
    b.planes = pl;
  }
  const inside = (p, skip) => {
    for (let i = 0; i < blocks.length; i++) {
      if (i === skip) continue;
      const pl = blocks[i].planes;
      let all = true;
      for (const q of pl) { if (dot(p, q.n) > q.d - 1e-4) { all = false; break; } }
      if (all) return true;
    }
    return false;
  };

  const pos = [], nrm = [], col = [], uvs = [];
  const attach = [];
  let minY = 1e9;

  blocks.forEach((b, bi) => {
    for (const f of b.faces) {
      const wf = f.map(p => xform(b.B, p, b.o));
      const n = faceNormal(wf);
      const c = centroid(wf);
      const R = Math.max(0.02, len(sub(wf[0], c)));

      /* Face colour. Dark base, with upward faces catching a little light and
         a turf film, and the recessed ones going darker still. */
      const up = clamp(n[1], -1, 1);
      const v = fbm(nz, mul(c, 1 / size), 2.2, 3, 0.55);
      let cc = mixc(pal.dark, pal.mid, sstep(-0.35, 0.55, v));
      cc = mixc(cc, pal.lit, sstep(0.10, 0.92, up) * (0.42 + 0.35 * v));
      cc = mulc(cc, 1 - sstep(0.0, -0.85, up) * 0.42);
      const film = sstep(0.30, 0.95, up) * sstep(0.30, 0.80, fbm(nz, mul(c, 1 / size), 3.6, 2) * 0.5 + 0.5);
      cc = mixc(cc, ALGAE, film * algae);
      if (fbm(nz, mul(c, 1 / size), 5.4, 2) > 0.42)
        cc = mixc(cc, CORALLINE, 0.30 * sstep(-0.2, 0.7, up));

      /* Fan-triangulate from the centroid and displace only that centroid.
         Rim vertices are left exactly where the cut put them, so blocks stay
         watertight and the creases stay sharp, but each face picks up a shallow
         relief instead of reading as a flat CG plane. */
      const bump = (fbm(nz, mul(c, 1 / size), 4.8, 2) * 0.5) * rough * R * 2.2;
      const cm = add(c, mul(n, bump));
      const nf = f.length;
      for (let i = 0; i < nf; i++) {
        const a = wf[i], bb = wf[(i + 1) % nf];
        const tn = norm(cross(sub(bb, a), sub(cm, a)));
        for (const p of [a, bb, cm]) {
          pos.push(p[0] * size, p[1] * size, p[2] * size);
          nrm.push(tn[0], tn[1], tn[2]);
          col.push(cc[0], cc[1], cc[2]);
          uvs.push((p[0] + p[2]) * 0.5, p[1]);
          if (p[1] * size < minY) minY = p[1] * size;
        }
      }

      /* Attachment points: upward-ish faces of usable area that are not
         buried inside a neighbouring block. */
      if (up > 0.24 && R > 0.09 && !inside(c, bi)) {
        attach.push({ p: [c[0] * size, c[1] * size, c[2] * size], n: n.slice(), area: R * R * Math.PI * size * size });
      }
    }
  });

  /* Normalise so `size` is the rock's half-width, then seat it on y = 0 so
     it drops straight onto a terrain sample. */
  const P = new Float32Array(pos);
  let ext = 1e-6;
  for (let i = 0; i < P.length; i += 3) ext = Math.max(ext, Math.abs(P[i]), Math.abs(P[i + 2]));
  const K = size / ext;
  for (let i = 0; i < P.length; i += 3) { P[i] *= K; P[i + 1] *= K; P[i + 2] *= K; }
  minY *= K;
  for (const a of attach) { a.p[0] *= K; a.p[1] *= K; a.p[2] *= K; a.area *= K * K; }
  for (let i = 1; i < P.length; i += 3) P[i] -= minY;
  for (const a of attach) a.p[1] -= minY;

  let x0 = 1e9, x1 = -1e9, y1 = -1e9, z0 = 1e9, z1 = -1e9;
  for (let i = 0; i < P.length; i += 3) {
    if (P[i] < x0) x0 = P[i]; if (P[i] > x1) x1 = P[i];
    if (P[i + 1] > y1) y1 = P[i + 1];
    if (P[i + 2] < z0) z0 = P[i + 2]; if (P[i + 2] > z1) z1 = P[i + 2];
  }
  attach.sort((a, b) => b.area - a.area);

  return {
    positions: P,
    normals: new Float32Array(nrm),
    colors: new Float32Array(col),
    uvs: new Float32Array(uvs),
    indices: null,                                /* non-indexed: faces must not share normals */
    attach, kind, seed, size,
    verts: P.length / 3, tris: P.length / 9,
    bounds: { w: x1 - x0, h: y1, d: z1 - z0 },
    ms: (typeof performance !== 'undefined' ? performance : Date).now() - t0
  };
}

/* pass your own THREE in — this file never imports it */
function toBufferGeometry(THREE, r) {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(r.positions, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(r.normals, 3));
  g.setAttribute('color', new THREE.BufferAttribute(r.colors, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(r.uvs, 2));
  g.computeBoundingSphere();
  return g;
}

/* Pick n attachment points spaced at least `minGap` apart, largest first.
   Useful for dropping coral onto a rock without piling it in one spot. */
function attachPoints(r, n, minGap) {
  const gap = minGap !== undefined ? minGap : r.size * 0.22;
  const out = [];
  for (const a of r.attach) {
    if (out.length >= n) break;
    let ok = true;
    for (const b of out) if (len(sub(a.p, b.p)) < gap) { ok = false; break; }
    if (ok) out.push(a);
  }
  return out;
}

const SeaRocks = { build, toBufferGeometry, attachPoints, KINDS, PALETTES, DEFAULTS };

/* The only edit against upstream: ESM exports, so this can be imported without
   `allowJs` and without a global side-channel. Everything above is verbatim. */
export { build, toBufferGeometry, attachPoints, KINDS, PALETTES, DEFAULTS };
export default SeaRocks;
